import { useState } from 'react';
import { Button, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../../components/common';
import { EditorDialog } from '../../../components/registry';
import { useBuddyWrite } from '../../api/client';
import type { FeatureOverrides, ModelPricing, ModelSettings } from '../../api/setup';
import { FeatureFlagsEditor, ModelSettingsEditor } from './settings';
import { messageOf, statusOf, useSetup } from './shared';

const EMPTY_PRICE: ModelPricing = {
  model: '',
  provider: 'vertex',
  inputPrice: 0,
  cachedInputPrice: 0,
  outputPrice: 0,
  tokenFactor: 1,
};

type PriceDraft = Record<
  'model' | 'provider' | 'inputPrice' | 'cachedInputPrice' | 'outputPrice' | 'tokenFactor',
  string
>;

const toDraft = (p: ModelPricing): PriceDraft => ({
  model: p.model,
  provider: p.provider ?? '',
  inputPrice: String(p.inputPrice),
  cachedInputPrice: p.cachedInputPrice == null ? '' : String(p.cachedInputPrice),
  outputPrice: String(p.outputPrice),
  tokenFactor: String(p.tokenFactor),
});

/** The POST body of a price draft, or the field errors. */
export function priceBody(d: PriceDraft): { body: ModelPricing | null; problems: Partial<PriceDraft> } {
  const problems: Partial<PriceDraft> = {};
  const num = (k: keyof PriceDraft, optional = false) => {
    const t = d[k].trim();
    if (t === '' && optional) return undefined;
    const n = Number(t);
    if (t === '' || !Number.isFinite(n) || n < 0) problems[k] = 'a number ≥ 0';
    return n;
  };
  const body: ModelPricing = {
    model: d.model.trim(),
    provider: d.provider.trim() || undefined,
    inputPrice: num('inputPrice') ?? 0,
    cachedInputPrice: num('cachedInputPrice', true),
    outputPrice: num('outputPrice') ?? 0,
    tokenFactor: num('tokenFactor') ?? 1,
  };
  if (!body.model) problems.model = 'required';
  if (!problems.tokenFactor && body.tokenFactor <= 0) problems.tokenFactor = 'greater than 0';
  return { body: Object.keys(problems).length ? null : body, problems };
}

function PriceEditor({
  initial,
  isNew,
  onSave,
  onClose,
}: {
  initial: ModelPricing;
  isNew: boolean;
  onSave: (p: ModelPricing) => Promise<void>;
  onClose: () => void;
}) {
  const [d, setD] = useState(toDraft(initial));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { body, problems } = priceBody(d);
  const field = (k: keyof PriceDraft, label: string, hint?: string) => (
    <TextField
      label={label}
      type="number"
      inputMode="decimal"
      step="any"
      value={d[k]}
      onChange={(e) => setD({ ...d, [k]: e.target.value })}
      error={d[k] !== '' ? problems[k] : undefined}
      hint={hint}
    />
  );
  return (
    <EditorDialog
      title={isNew ? 'New model price' : `Price of ${initial.model}`}
      error={error}
      busy={busy}
      canSave={!!body}
      onSave={() => {
        if (!body) return;
        setBusy(true);
        setError(null);
        onSave(body)
          .then(onClose)
          .catch((e) => setError(messageOf(e)))
          .finally(() => setBusy(false));
      }}
      onClose={onClose}
    >
      <TextField
        label="Model"
        value={d.model}
        onChange={(e) => setD({ ...d, model: e.target.value })}
        disabled={!isNew}
        hint={isNew ? 'as Vertex names it, e.g. gemini-3.8-flash or claude-sonnet-5-5' : undefined}
        required
      />
      <TextField
        label="Provider"
        value={d.provider}
        onChange={(e) => setD({ ...d, provider: e.target.value })}
        hint="vertex (Gemini) or anthropic (Claude, whose cache writes cost 1.25 × input)"
      />
      {field('inputPrice', 'Input $/1M')}
      {field('cachedInputPrice', 'Cached input $/1M', 'empty = the input price')}
      {field('outputPrice', 'Output $/1M')}
      {field('tokenFactor', 'Token factor', 'policy multiplier on top of the prices (1 = none)')}
    </EditorDialog>
  );
}

const RATE_MAX = 1000;

/** Global engine settings and feature flags (the roots of both cascades), the NYCE token rate and model pricing. */
export function BuddyModelsPage() {
  const { api } = useSetup();
  const canWrite = useBuddyWrite();
  const ro = !canWrite;
  const global = useAsync(() => api.globalSettings.get(), [api]);
  const pricing = useAsync(() => api.pricing.list(), [api]);
  const [draft, setDraft] = useState<Partial<ModelSettings> | null>(null);
  const [featureDraft, setFeatureDraft] = useState<FeatureOverrides | null>(null);
  const [rateDraft, setRateDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [price, setPrice] = useState<{ value: ModelPricing; isNew: boolean } | null>(null);
  const toast = useNotice();

  const g = global.data;
  const settings = draft ?? g?.settings ?? {};
  const features = featureDraft ?? g?.features ?? {};
  const rate = rateDraft ?? (g?.nyceTokenRatePerMillion != null ? String(g.nyceTokenRatePerMillion) : '');
  const rateNumber = Number(rate);
  const rateValid =
    rate.trim() !== '' && Number.isFinite(rateNumber) && rateNumber > 0 && rateNumber <= RATE_MAX;
  const rateDirty = rateDraft !== null && rateNumber !== g?.nyceTokenRatePerMillion;
  const settingsDirty = !!draft && JSON.stringify(draft) !== JSON.stringify(g?.settings);
  const featuresDirty = !!featureDraft && JSON.stringify(featureDraft) !== JSON.stringify(g?.features ?? {});
  const dirty = !!g && (settingsDirty || featuresDirty || (rateDirty && rateValid));

  const discard = () => {
    setDraft(null);
    setFeatureDraft(null);
    setRateDraft(null);
  };
  const saveSettings = async () => {
    if (!g) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.globalSettings.put({
        ...(settingsDirty ? { settings: draft! } : {}),
        ...(featuresDirty ? { features: featureDraft! } : {}),
        ...(rateDirty && rateValid ? { nyceTokenRatePerMillion: rateNumber } : {}),
        version: g.version,
      });
      global.setData(saved);
      discard();
      toast.success(`Saved the global settings (version ${saved.version})`);
    } catch (e) {
      if (statusOf(e) === 409) setConflict(true);
      else setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };
  const reloadAfterConflict = async () => {
    discard();
    setConflict(false);
    await global.reload();
  };
  const savePrice = async (p: ModelPricing) => {
    await api.pricing.upsert(p);
    toast.success(`Saved the price of ${p.model}`);
    await pricing.reload();
  };
  const removePrice = async (p: ModelPricing) => {
    try {
      await api.pricing.remove(p.model);
      toast.success(`Removed the price of ${p.model}`);
      await pricing.reload();
    } catch (e) {
      toast.error(messageOf(e));
    }
  };

  return (
    <Page
      title="Models"
      description="Global engine settings and features every organization, tenant and system inherits, and the price list behind cost and NYCE tokens."
      actions={
        canWrite && (
          <>
            {dirty && (
              <Button variant="ghost" onClick={discard}>
                Discard
              </Button>
            )}
            <Button onClick={() => void saveSettings()} loading={saving} disabled={!dirty || conflict}>
              Save global settings
            </Button>
          </>
        )
      }
    >
      <ErrorBox error={global.error ?? error} />
      {conflict && (
        <div className="nyk-notice nyk-notice-warn" role="alert" data-testid="version-conflict">
          Someone else saved the global settings since you opened them (version {g?.version}). Reload to see
          their version — your unsaved changes here are dropped.{' '}
          <Button size="sm" variant="secondary" onClick={() => void reloadAfterConflict()}>
            Reload
          </Button>
        </div>
      )}
      {toast.element}
      <Loading loading={global.loading && !g} />
      {g && (
        <section className="nyk-card" data-testid="global-settings">
          <div className="nyk-card-head">
            <h2>Global engine settings</h2>
            <span className="nyk-ui-hint">
              version {g.version}
              {g.updatedAt ? ` · ${new Date(g.updatedAt).toLocaleString()}` : ''}
              {g.updatedBy ? ` by ${g.updatedBy}` : ''}
            </span>
          </div>
          <ModelSettingsEditor
            value={settings}
            onChange={(v) => setDraft(v as Partial<ModelSettings>)}
            allRequired
            disabled={ro}
          />
        </section>
      )}
      {g && (
        <section className="nyk-card">
          <div className="nyk-card-head">
            <h2>Global feature flags</h2>
            <span className="nyk-ui-hint">
              inherited unless an organization, tenant or system overrides them
            </span>
          </div>
          <FeatureFlagsEditor value={features} onChange={setFeatureDraft} allRequired disabled={ro} />
        </section>
      )}
      {g && (
        <section className="nyk-card" data-testid="nyce-rate">
          <div className="nyk-card-head">
            <h2>NYCE token rate</h2>
            <span className="nyk-ui-hint">USD per 1M NYCE tokens — what a sold budget is worth</span>
          </div>
          <p className="nyk-card-intro">
            A NYCE token is a unit of money: a turn costs the budget what it really costs, whatever it is
            spent on. At {rateValid ? rateNumber : '—'} USD per 1M, a 50M tokens a month agreement is{' '}
            {rateValid ? `$${(rateNumber * 50).toFixed(2)}` : '—'} of model spend a month. Raising the rate
            makes every budget go further.
          </p>
          <div className="nyk-form">
            <TextField
              label="USD per 1M NYCE tokens"
              type="number"
              inputMode="decimal"
              step="any"
              value={rate}
              onChange={(e) => setRateDraft(e.target.value)}
              disabled={ro}
              error={rate.trim() !== '' && !rateValid ? `greater than 0 and at most ${RATE_MAX}` : undefined}
            />
          </div>
        </section>
      )}
      <section className="nyk-card">
        <div className="nyk-card-head">
          <h2>Model pricing</h2>
          {canWrite && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPrice({ value: EMPTY_PRICE, isNew: true })}
            >
              Add price
            </Button>
          )}
        </div>
        <p className="nyk-card-intro">
          USD per 1M tokens. The prices decide how many NYCE tokens a model costs; the token factor is a
          policy multiplier only (margin, or a model to discourage).
        </p>
        <ErrorBox error={pricing.error} />
        {pricing.data && !pricing.data.length && (
          <EmptyState
            title="No prices yet."
            hint="A model without a price is billed at the reference price list."
          />
        )}
        {!!pricing.data?.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="pricing-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Provider</th>
                  <th className="nyk-ui-num">Input</th>
                  <th className="nyk-ui-num">Cached input</th>
                  <th className="nyk-ui-num">Output</th>
                  <th className="nyk-ui-num">Token factor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pricing.data.map((p) => (
                  <tr key={p.model} data-model={p.model}>
                    <td className="nyk-card-title">
                      <b>{p.model}</b>
                    </td>
                    <td data-label="Provider">{p.provider ?? '—'}</td>
                    <td className="nyk-ui-num" data-label="Input">
                      {p.inputPrice}
                    </td>
                    <td className="nyk-ui-num" data-label="Cached input">
                      {p.cachedInputPrice ?? '—'}
                    </td>
                    <td className="nyk-ui-num" data-label="Output">
                      {p.outputPrice}
                    </td>
                    <td className="nyk-ui-num" data-label="Token factor">
                      {p.tokenFactor}
                    </td>
                    <td className="nyk-row-actions">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPrice({ value: p, isNew: false })}
                          >
                            Edit
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="danger"
                            message={`Remove the price of ${p.model}? It is then billed at the reference price list.`}
                            onConfirm={() => void removePrice(p)}
                          >
                            Delete
                          </ConfirmButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </section>
      {price && (
        <PriceEditor
          key={price.value.model || 'new'}
          initial={price.value}
          isNew={price.isNew}
          onSave={savePrice}
          onClose={() => setPrice(null)}
        />
      )}
    </Page>
  );
}
